'use client';

import {
	BigNum,
	BN,
	calculateBorrowRate,
	calculateDepositRate,
	calculateUtilization,
	calculateWithdrawLimit,
	getTokenAmount,
	QUOTE_PRECISION_EXP,
	SPOT_MARKET_RATE_PRECISION,
	SPOT_MARKET_RATE_PRECISION_EXP,
	SpotBalanceType,
	SpotMarketAccount,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { MarketId, matchEnum } from '@drift/common';
import { useCallback } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import NumLib from 'src/utils/NumLib';
import { getApyFromApr } from 'src/utils/borrowLend';
import { nowTs } from 'src/utils/dateTime';
import { useAggregateAccountsSpotBalances } from './useAccountBankBalances';
import useDriftClientIsReady from './useDriftClientIsReady';
import useGetOraclePriceForMarket from './useGetOraclePriceForMarket';
import useUiUpdateInterval from './useUiUpdateInterval';
import { useSpotMarkets } from './spotMarkets/useSpotMarkets';

export type BorrowMarketData = {
	bankConfig: SpotMarketConfig;
	bankIndex: number;
	symbol: string;
	totalDepositsBase: BigNum;
	totalDepositsQuote: BigNum;
	depositApr: BigNum;
	depositApy: BigNum;
	totalBorrowsBase: BigNum;
	totalBorrowsQuote: BigNum;
	borrowApr: BigNum;
	totalUserBorrowsBase: BigNum;
	totalUserBorrowsQuote: BigNum;
	totalUserDepositsBase: BigNum;
	totalUserDepositsQuote: BigNum;
	totalUserNetBalanceBase: BigNum;
	totalUserNetBalanceQuote: BigNum;
	borrowLimitBase: BigNum;
	borrowLimitQuote: BigNum;
	borrowUtilization: BigNum;
	userBorrowAccounts: number;
	userDepositAccounts: number;
	assetWeight: number;
	liabilityWeight: number;
	maintenanceAssetWeight: number;
	maintenanceLiabilityWeight: number;
	spotMarketAccount: SpotMarketAccount;
	fuelBoostDeposits?: number;
	fuelBoostBorrows?: number;
	fuelBoostInsurance?: number;
};

/* Used for global balances + aggregated current user balances */
const useSyncBorrowLendBalances = () => {
	const spotMarkets = useSpotMarkets();
	const set = useDriftStore((s) => s.set);
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftStore((s) => s.driftClient.client);

	const userBankBalances = useAggregateAccountsSpotBalances(undefined);

	const getOraclePriceHook = useGetOraclePriceForMarket();

	const getOraclePrice = (bank: SpotMarketConfig) => {
		return getOraclePriceHook(MarketId.createSpotMarket(bank.marketIndex));
	};

	const updateBorrowData = useCallback(() => {
		if (!driftClientIsReady || !driftClient?.isSubscribed) {
			set((s) => {
				s.borrowLendData = [];
			});
			return;
		}

		const borrowLendBalances = spotMarkets
			.map((bank) => {
				let bankAccount: SpotMarketAccount | undefined;
				try {
					bankAccount = driftClient.getSpotMarketAccount(bank.marketIndex);
				} catch (e) {
					console.error(e);
					return;
				}

				if (!bankAccount) return;

				const totalDepositsBase = BigNum.from(
					getTokenAmount(
						bankAccount.depositBalance,
						bankAccount,
						SpotBalanceType.DEPOSIT
					),
					bank.precisionExp
				);

				const totalBorrowsBase = BigNum.from(
					getTokenAmount(
						bankAccount.borrowBalance,
						bankAccount,
						SpotBalanceType.BORROW
					),
					bank.precisionExp
				);

				const oraclePrice = getOraclePrice(bank);

				if (!oraclePrice) return;

				const userBankBorrowBalances = userBankBalances?.filter(
					(bal) =>
						bal.asset.marketIndex === bank.marketIndex &&
						matchEnum(bal.balanceType, SpotBalanceType.BORROW)
				);

				const borrowAccountIds = userBankBorrowBalances
					.filter((borrowBal) => !borrowBal.balance.eqZero())
					.map((borrowBal) => borrowBal.accountId);

				const userBankDepositBalances = userBankBalances?.filter(
					(bal) =>
						bal.asset.marketIndex === bank.marketIndex &&
						matchEnum(bal.balanceType, SpotBalanceType.DEPOSIT)
				);

				const depositAccountIds = userBankDepositBalances
					.filter((depositBal) => !depositBal.balance.eqZero())
					.map((depositBal) => depositBal.accountId);

				const totalUserBorrowsBase = NumLib.sumBigNums(
					userBankBorrowBalances?.map((borrowBal) => borrowBal.balance),
					bank.precisionExp
				);

				const totalUserDepositsBase = NumLib.sumBigNums(
					userBankDepositBalances?.map((depositBal) => depositBal.balance),
					bank.precisionExp
				);

				const totalUserBorrowsQuote = totalUserBorrowsBase
					.shiftTo(QUOTE_PRECISION_EXP)
					.mul(oraclePrice);

				const totalUserDepositsQuote = totalUserDepositsBase
					.shiftTo(QUOTE_PRECISION_EXP)
					.mul(oraclePrice);

				const borrowLimitBase = BigNum.from(
					calculateWithdrawLimit(bankAccount, new BN(nowTs())).borrowLimit,
					bank.precisionExp
				);

				const assetWeight = bankAccount.initialAssetWeight / 100;
				const liabilityWeight = bankAccount.initialLiabilityWeight / 100;

				const maintenanceAssetWeight = bankAccount.maintenanceAssetWeight / 100;
				const maintenanceLiabilityWeight =
					bankAccount.maintenanceLiabilityWeight / 100;

				const depositApr = BigNum.from(
					calculateDepositRate(bankAccount),
					SPOT_MARKET_RATE_PRECISION_EXP
				);
				const depositApy = getApyFromApr(depositApr.toNum(), 'hourly');
				const borrowMarketData: BorrowMarketData = {
					bankConfig: bank,
					bankIndex: bank.marketIndex,
					symbol: bank.symbol,
					totalDepositsBase,
					totalDepositsQuote: totalDepositsBase
						.shiftTo(QUOTE_PRECISION_EXP)
						.mul(oraclePrice)
						.shiftTo(QUOTE_PRECISION_EXP),
					depositApr: BigNum.from(
						calculateDepositRate(bankAccount),
						SPOT_MARKET_RATE_PRECISION_EXP
					),
					depositApy: BigNum.from(
						depositApy * SPOT_MARKET_RATE_PRECISION.toNumber(),
						SPOT_MARKET_RATE_PRECISION_EXP
					),
					totalBorrowsBase,
					totalBorrowsQuote: totalBorrowsBase
						.shiftTo(QUOTE_PRECISION_EXP)
						.mul(oraclePrice)
						.shiftTo(QUOTE_PRECISION_EXP),
					borrowApr: BigNum.from(
						calculateBorrowRate(bankAccount),
						SPOT_MARKET_RATE_PRECISION_EXP
					),
					totalUserBorrowsBase,
					totalUserBorrowsQuote:
						totalUserBorrowsQuote.shiftTo(QUOTE_PRECISION_EXP),
					totalUserDepositsBase,
					totalUserDepositsQuote:
						totalUserDepositsQuote.shiftTo(QUOTE_PRECISION_EXP),
					totalUserNetBalanceBase:
						totalUserDepositsBase.sub(totalUserBorrowsBase),
					totalUserNetBalanceQuote: totalUserDepositsQuote
						.sub(totalUserBorrowsQuote)
						.shiftTo(QUOTE_PRECISION_EXP),
					borrowLimitBase,
					borrowLimitQuote: borrowLimitBase
						.shiftTo(QUOTE_PRECISION_EXP)
						.mul(oraclePrice)
						.shiftTo(QUOTE_PRECISION_EXP),
					borrowUtilization: BigNum.from(
						calculateUtilization(bankAccount),
						6
						// SPOT_MARKET_UTILIZATION_PRECISION_EXP
					),
					userBorrowAccounts: borrowAccountIds.length,
					userDepositAccounts: depositAccountIds.length,
					assetWeight,
					liabilityWeight,
					maintenanceAssetWeight,
					maintenanceLiabilityWeight,
					spotMarketAccount: bankAccount,
					fuelBoostDeposits: bankAccount.fuelBoostDeposits,
					fuelBoostBorrows: bankAccount.fuelBoostBorrows,
					fuelBoostInsurance: bankAccount.fuelBoostInsurance,
				};

				return borrowMarketData;
			})
			.filter((bank) => !!bank);

		set((s) => {
			s.borrowLendData = borrowLendBalances;
		});
	}, [driftClientIsReady, driftClient, userBankBalances]);

	useUiUpdateInterval(updateBorrowData, false, true);
};

export default useSyncBorrowLendBalances;
